/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import { Train }           from '@/models/scenario/train';
import { ViewerInterface } from './viewer.interface';
import { Scenario }        from '@/models/scenario/scenario';
import { InterLink }       from '@/models/scenario/inter.link';
import { Track }           from '@/models/scenario/track';
import { ViewMemento }     from '@/screens/ide/stringviewer/view.memento';
import { MeshLineBuilder } from "@/helpers/lines/mesh.line.builder";

import {
  Mesh,
  Color
}                          from 'three';


/** go / no go parking bucket in each interlink */
export interface InterLinkPark {

  /** the interlink */
  link: InterLink;

  /** allowed */
  allowed: {train: Train, track: Track, reason: string}[];

  /** denied */
  denied: {train: Train, track: Track, reason: string}[];

  /** the top (higher) distance */
  top: number;

  /** the bottom (lower) distance */
  bottom: number;
}

/**
 *
 * @class ParkabilityManager
 *
 * @brief helper to draw the "extra" info for parkability after the limit line cursor (if enabled)
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class ParkabilityManager {

  /** @brief _viewerViewerInterface the main viewer of the graph */
  private _viewer: ViewerInterface = null;

  /** the viewer windows have to have a unique DOM ID since there may be more than one viewer in the browser */
  private    _uuid: number          = 0;

  /** the data model for the physical network and trains */
  private _scenario: Scenario       = null;

  /** the lookup table for parking, ordered highest mileage first [0] */
  private _slots: InterLinkPark[]   = [];

  /** the most westward mileage (top of graph) */
  private _maxMile: number          = 0;

  /** the interlinks where we can park */
  private _links: InterLink[]       = [];

  /** the trains that would want to park */
  private _trains: Train[]          = [];

  /** the color for the allowed blocks */
  private _allowedColor: string     = 'green';

  /** the color for the denied blocks */
  private _deniedColor: string      = 'red';

  /** the thickness of the extra info geometry */
  private _thickness: number        = 50;

  /** the world time asis (x) position */
  private _wt: number               = 0.0;

  /** indicator for having a hover (or not) */
  private _hover: {slot: InterLinkPark, allowed: boolean} = null;

  /** @brief standard constructor */
  constructor(viewer: ViewerInterface, scenario: Scenario, uuid: number) {

    this._viewer   = viewer;
    this._uuid     = uuid;
    this._scenario = scenario;

    this.setScenario(this._scenario);
  }

  /** update the data model */
  setScenario(scenario: Scenario) {

    this._scenario = scenario;
    this._links    = [];
    this._maxMile  = 0;

    if (this._scenario == null) {

      return ;
    }

    this._links    = this._scenario.interlinks();

    /* find the max mileage; interlinks are ordered so this should just be the first interlink mileage */


    this._maxMile = Math.max(this._links[0]._before._mileage, this._links[0]._after ? this._links[0]._after._mileage: 0);
  }

  /** clear all train parking status */
  clear() {

    this._slots.forEach((item: InterLinkPark) => {

      item.allowed = [];
      item.denied  = [];

    });

    this._trains   = [];
  }

  /** test all the parking slots */
  setTrains(trains: Train[]) {

    const pm: ParkabilityManager = this;

    pm.clear();

    pm._trains = trains;
    pm._slots  = [];

    /* for each train, walk the interlinks and figure out which tracks the train can or can't use */

    pm._links.forEach((bundle: InterLink, i: number) => {

      if ((bundle._before == null)||(bundle._after == null)) {

        /* has to have a decision point on both sides */

        return ;
      }

      const nextBundle: InterLink = ((i + 1) < pm._links.length) ? pm._links[i + 1] : null;

      const slot: InterLinkPark = {
        link:    bundle,
        allowed: [],
        denied:  [],
        top:     bundle.maxMiles(),
        bottom:  (nextBundle != null) ? nextBundle.maxMiles() : 0
      };

      /* for each train test it on all tracks in this bundle */

      pm._trains.forEach((train: Train, j: number) => {

        bundle._tracks.forEach((track: Track, k: number) => {

          const weight: number = track._maxWeight;
          const length: number = Math.round(track._useLength * 5280);

          if ((weight != 0) && (train._tonnage > weight)) {

            slot.denied.push({
              train, track, reason: `WT ${train._tonnage} > ${weight}`
            });

            return ;
          }

          if ((length != 0) && (train._length > length)) {

            slot.denied.push({
              train, track, reason: `LT ${train._length} > ${length}`
            });

            return ;
          }

          /* annie it's ok! */

          slot.allowed.push({
            train, track, reason: 'ok'
          });

        });

      });

      /* save this slots */

      pm._slots.push(slot);
    });
  }

  /** test if we currently have a hover indicator */
  hasHover(): boolean {

    return this._hover != null;

  }

  /** fetch the hover if there is one */
  hover(): {slot: InterLinkPark, allowed: boolean} {

    return this._hover;
  }

  /** indicate the user is hovering over this allowed or denied block */
  addHover(slot: InterLinkPark, allowed: boolean) {

    this._hover = {slot, allowed};
  }

  /** remove any hover position */
  clearHover() {

    this._hover = null;
  }

  /** if there is a park bounding box that contains this point, return it. */
  contains(x: number, y: number): {slot: InterLinkPark, allowed: boolean} {

    const pm: ParkabilityManager = this;

    /* before looking for a hit, determine left / right bounds for the column of boxes */

    const vm: ViewMemento = this._viewer.viewMemento();
    const tx1: number     = pm._viewer.screenToWorld( 0     , 0)[0];
    const tx2: number     = pm._viewer.screenToWorld( this._thickness, 0)[0];

    const width: number   = Math.abs(tx2 - tx1);

    const left: number    = this._wt;
    const right: number   = this._wt + (width / vm._timeScale);
    const middle: number  = left + ((right - left) / 2.0);

    const wx: number = x / vm._timeScale;
    const wy: number = y / vm._distanceScale;

    /* can we exit early? */

    if ((wx < left) || (wx > right)) {

      /* no match in entire column */

      return null;
    }

    if ((wy > this._slots[0].top) || (wy < 0)) {

      /* vertically off-screen */

      return null;
    }

    /* walk the slots looking for one that contains point */

    let slot: InterLinkPark = null;
    let allowed: boolean   = true;

    this._slots.forEach((item: InterLinkPark ) => {

      if (slot != null) {

        return ;
      }

      if ((item.top >= wy) && (item.bottom <= wy)) {

        slot    = item;
        allowed = (wx <= middle);
      }

    });

    /* pass back any hit */

    return (slot == null) ? null : {slot, allowed};
  }

  /** generate the "boxes" that make up the parkability hover zones */
  generate(wt: number, thickness: number): Mesh[] {

    this._thickness = thickness;
    this._wt        = wt;

    const    pm: ParkabilityManager = this;
    let results: Mesh[]             = [];

    /* figure out how wide the line should be in world space, to always be a fixed thickness on screen */

    const vm: ViewMemento = pm._viewer.viewMemento();
    const tx1: number     = pm._viewer.screenToWorld( 0     , 0)[0];
    const tx2: number     = pm._viewer.screenToWorld( thickness, 0)[0];

    thickness = Math.abs(tx2 - tx1);

    /*
     * we draw at the limit line, and nudge it over so that our poly lines are on
     * the right side of the limit line.
     *
     */

    /*
     * rendering: we can't just draw a box in Three.js; it's in world space, and
     * as we scale up or down the zoom level world space scales with it (that's how 3D works!)
     * So if you zoom in...you will get a comically big box or line.  We generally
     * get around this in our main train line plot, but using 1px lines...which are always
     * drawn as 1px by WebGL no matter the zoom level.
     *
     * But now how to draw anything with thickness but keep at the same thickness (screen
     * space) regardless of scale/zoom?
     *
     * For that, we use poly line, it can draw lines of various thickness for us, and any
     * time we change the scale...we just redraw the whole thing and as we do that we
     * calculate the thickness as it should be for the current scale/zoom...so that its always
     * having the same visual thickness.
     *
     * To draw our boxes...we just use a poly line that is as wide as the box we need :)
     *
     */

    /*
     * the render loop: walk through the interlinks (they are ordered high mileage to low),
     * and for each one draw blue box for when our trains can park there, and a yellow box
     * for when trains can not park there.
     *
     */

    pm._slots.forEach( (link: InterLinkPark, i: number) => {

      [link.allowed, link.denied].forEach((item: {train: Train, track: Track, reason: string}[], j: number) => {

        if (item.length == 0) {

          return ;
        }

        let    color: string         = (j == 0) ? pm._allowedColor : pm._deniedColor;
        const offset: number        = (j == 0) ? 0                : thickness / 2;

        const sx: number            = this._wt    * vm._timeScale + offset + thickness / 4;
        const sy: number            = link.top    * vm._distanceScale;
        const ex: number            = this._wt    * vm._timeScale + offset + thickness / 4;
        const ey: number            = link.bottom * vm._distanceScale;

        /* if this box is being hovered over, color it with the highlighting color */

        if (pm.hasHover()) {

          if (pm._hover.slot == link) {

            if ((j == 0) && pm._hover.allowed) {

              color = 'blue';
            }

            if ((j == 1) && !this._hover.allowed) {

              color = 'blue';
            }
          }
        }

        /* generate the polyline  */

        let lineMesh: Mesh = (new MeshLineBuilder())
          .setColor( new Color(color))
          .setThickness(pm._thickness)
          .setOpacity(1.0)
          .buildFromLines([[sx, sy, ex, ey]], pm._viewer.width(), pm._viewer.height());

        lineMesh.geometry.name                = `parkability-go-${sy}`;

        /* save the mesh for this box */

        results.push(lineMesh);

      });

    });

    /* all done */

    return results;
  }


}
